<template>
<div class="chapter">
<div class="inner_page">
<h2 class="calibre" id="pgepubid00032"><a class="calibre1" id="page1"></a><span class="calibre1" id="pgepubid00033" title="p. 1"></span>I.—Neglected Children.</h2>
<h3 class="calibre17" id="pgepubid00034">CHAPTER I.<br class="calibre8"/>
<span class="gutsmall1">STARTLING FACTS.</span></h3>
<p class="gutsumm"><i class="calibre18">The Pauper Population</i>.—<i class="calibre18">Pauper
Children</i>.—<i class="calibre18">Opinions concerning their proper
Treatment</i>.—<i class="calibre18">A Hundred Thousand Children loose in
London Streets</i>.—<i class="calibre18">Neglected
Babies</i>.—<i class="calibre18">Juvenile</i> “<i class="calibre18">Market
Prowlers</i>.”</p>
<p class="calibre3"><span class="smcap">It</span> is a startling fact that, in
England and Wales alone, at the present time, the number of
children under the age of sixteen, dependent more or less on the
parochial authorities for maintenance, amounts to three hundred
and fifty thousand.</p>
<p class="calibre3">It is scarcely less startling to learn that annually more than
a hundred thousand criminals emerge at the doors of the various
prisons, that, for short time or long time, have been their
homes, and with no more substantial advice than “to take
care that they don’t make their appearance there
again,” are turned adrift once more to face the world,
unkind as when they last stole from it.  This does not
include our immense army of juvenile vagrants.  How the
information has been arrived at is more than I can tell; but it
is an accepted fact that, <a id="page2"></a><span id="pgepubid00035" title="p. 2"></span>daily, winter and summer, within the
limits of our vast and wealthy city of London, there wander,
destitute of proper guardianship, food, clothing, or employment,
a <i class="calibre18">hundred thousand</i> boys and girls in fair training for the
treadmill and the oakum shed, and finally for Portland and the
convict’s mark.</p>
<p class="calibre3">It is these last-mentioned hundred thousand, rather than the
four hundred and fifty thousand previously mentioned, that are
properly classed under the heading of this first chapter. 
Practically, the three hundred and fifty thousand little paupers
that cumber the poor-rates are without the category of neglected
ones.  In all probability, at least one-half of that vast
number never were victims of neglect, in the true sense of the
term.  Mr. Bumble derives his foster children from sources
innumerable.  There are those that are born in the
“house,” and who, on some pretext, are abandoned by
their unnatural mother.  There are the “strays,”
discovered by the police on their beats, and consigned, for the
present, to the workhouse, and never owned.  There is the
offspring of the decamping weaver, or shoemaker, who goes on
tramp “to better himself;” but, never succeeding,
does not regard it as worth while to tramp home again to report
his ill-luck.  These, and such as these, may truly ascribe
their pauperism to neglect on somebody’s part; but by far
the greater number are what they are through sheer
misfortune.  When death snatches father away from the table
scarcely big enough to accommodate the little flock that cluster
about it—snatches him away in the lusty prime of <a id="page3"></a><span id="pgepubid00036" title="p. 3"></span>life, and
without warning, or, worse still, flings him on a bed of
sickness, the remedies for which devour the few pounds thriftily
laid aside for such an emergency, and, after all, are of no
avail, what other asylum but the workhouse offers itself to
mother and children?  How many cases of this kind the parish
books could reveal, one can only guess; quite enough, we may be
sure, to render unpalatable that excessive amount of caution
observed by those in power against “holding out a
premium” to pauperism.  It is somewhat amazing to hear
great authorities talk sometimes.  Just lately, Mr. Bartley,
reading at the Society of Arts a paper entitled, “The
training and education of pauper children,” took occasion
to remark:—</p>
<blockquote class="calibre19"><div class="calibre2"><p class="calibre3">“These children cannot be looked upon
exactly in the same way as paupers proper, inasmuch as their
unfortunate position is entirely due to circumstances over which
they could have no control.  They are either the offspring
of felons, cripples, and idiots, or orphans, bastards, and
deserted children, and claim the protection of the law,
frequently from their tenderest years, from having been deprived
of the care of their natural guardians without fault or crime of
their own.  Such being their condition, they must either
steal or starve in the streets, or the State must take charge of
them.  It may further be affirmed that, in a strictly
commercial point of view, it is more economical to devote a
certain amount in education and systematic training than by
allowing them to grow up in the example of their parents and
workhouse companions, to render their permanent support, either
in a prison or a <a id="page4"></a><span id="pgepubid00037" title="p. 4"></span>workhouse, a burden on the industrious classes.  The
State, in fact, acknowledges this, and accordingly a provision is
theoretically supplied for all pauper children, not only for
their bodily wants, but, to a certain extent, for their mental
improvement.  At the same time, it is also necessary that
the extreme should not be run into, viz., that of treating them
so liberally as to hold out a premium to pauperism.  In no
case should their comfort be better than, nor in fact as good as,
an industrious labourer has within his reach.”</p>
</div>
</blockquote>
<p class="calibre3">Mr. Bartley is a gentleman whose knowledge of the subject he
treats of exceeds that of most men; moreover, he is a man who, in
his acts and nature, shows himself actuated by a kind heart,
governed by a sound head; but, with all deference, it is
difficult to agree altogether with the foregoing remarks of his:
and they are the better worth noticing, because precisely the
same sentiment breathes through almost every modern, new, and
improved system of parochial reform.  Why should these
unfortunate creatures, “their unfortunate position being
entirely due to circumstances over which they had no
control,” be made less comfortable in their condition than
the industrious labourer,—who, by the way, may be an
agricultural labourer, with his starvation wages of nine
shillings a week and his damp and miserable hovel of two rooms to
board and lodge his numerous family?  What sort of justice
is it to keep constantly before their unoffending eyes the
humiliating fact that they have no standing even on the bottom
round of the social ladder, and that their proper place is to
crouch meekly and uncomplainingly <a id="page5"></a><span id="pgepubid00038" title="p. 5"></span>at the foot of it?  Even
supposing that they, the pauper children, are “either the
offspring of felons, cripples, and idiots, or orphans, bastards,
and deserted children,” which is assuming to the verge of
improbability, still, since it is acknowledged that the state in
which we discover them “is due to no fault or crime of
their own,” why should we hesitate to make them commonly
comfortable?  To fail so to do when it is in our power, and
when, according to their innocence and helplessness, it is their
due, is decidedly at variance with the commonly-understood
principles of Christian charity.  It will be needless,
however, here to pursue the subject of pauper management, since
another section of this book has been given to its
consideration.  Anyhow, our three hundred and fifty thousand
pauper children can have no claim to be reckoned among the
“neglected.”  They are, or should be, a class
whose hard necessity has been brought under the notice of the
authorities, and by them considered and provided for.</p>
<p class="calibre3">There are other neglected children besides those already
enumerated, and who are not included in the tenth part of a
million who live in the streets, for the simple reason that they
are too young to know the use of their legs.  They are
“coming on,” however.  There is no present fear
of the noble annual crop of a hundred thousand diminishing. 
They are so plentifully propagated that a savage preaching
“civilization” might regard it as a mercy that the
localities of their infant nurture are such as suit the ravening
appetites of cholera and typhus.  Otherwise they would breed
like rabbits in an <a id="page6"></a><span id="pgepubid00039" title="p. 6"></span>undisturbed warren, and presently swarm so abundantly
that the highways would be over-run, making it necessary to pass
an Act of Parliament, improving on the latest enacted for dogs,
against the roaming at large of unmuzzled children of the
gutter.  Observe the vast number of “city
Arabs,” to be encountered in a walk, from Cheapside to the
Angel at Islington, say.  You cannot mistake them. 
There are other children who are constantly encountered in the
street, male and female, who, though perhaps neither so ragged
and dirty as the genuine juvenile vagrants, are even more sickly
and hungry looking; but it is as easy to distinguish between the
two types—between the <i class="calibre18">home-owning</i> and the
<i class="calibre18">homeless</i>, as between the sleek pet dog, and the cur of the
street, whose ideas of a “kennel” are limited to that
represented by the wayside gutter, from which by good-luck
edibles may be extracted.  Not only does the youthful
ragamuffin cry aloud for remedy in every street and public way of
the city, he thrusts his ugly presence on us continuously, and
appeals to us in bodily shape.  In this respect, the curse
of neglected children differs widely from any of the others,
beggars alone excepted, perhaps.  And even as regards
beggars, to see them is not always to believe in them as human
creatures helpless in the sad condition in which they are
discovered, and worthy of the best help we can afford to bestow
on them.  It is next to impossible by outward signs merely
to discriminate between the impostor and the really unfortunate
and destitute.  The pallid cheek and the sunken eye, may be
a work of art and not of nature, and in the <a id="page7"></a><span id="pgepubid00040" title="p. 7"></span>cunning
arrangement of rags, so as to make the most of them, the cheat
must always have an advantage over the genuine article. 
Weighing the evidence <i class="calibre18">pro.</i> and <i class="calibre18">con.</i>, the object of
it creeping even at his snail’s pace may be out of sight
before we arrive at what appears to us a righteous verdict, and
our scrupulous charity reserved for another, occasion.  But
no such perplexing doubts and hesitation need trouble us in
selecting the boy gutter bred and born from the one who lays
claim to a home, even though it may be no more than a feeble
pretence, consisting of a family nightly gathering in some dirty
sty that serves as a bedroom, and a morning meeting at a board
spread with a substitute for a breakfast.  In the latter
there is an expression of countenance utterly wanting in the
former; an undescribable shyness, and an instinctive observance
of decency, that has been rain-washed and sun-burnt out of the
gipsy of the London highway since the time of his crawling out of
the gooseberry sieve, with a wisp of hay in it that served him as
a cradle.</p>
<p class="calibre3">And here I can fancy I hear the incredulous reader exclaim,
“But that is mere imagery of course; ragamuffin babies
never are cradled in gooseberry sieves, with a wisp of hay to lie
on.”  Let me assure you, dear madam, it is not
imagery, but positive fact.  The strangest receptacles do
duty as baby cradles at times.  In another part of our book,
it will be shown that a raisin-box may be so adapted, or even an
egg-box; the latter with a bit of straw in it as a cradle for an
invalid baby with a broken thigh!  But as regards the
gooseberry sieve, it is a fact that came under <a id="page8"></a><span id="pgepubid00041" title="p. 8"></span>the
writer’s immediate observation.  Accompanied by a
friend, he was on a visit of exploration into the little-known
regions of Baldwin’s Gardens, in Leather Lane, and entering
a cellar there, the family who occupied it were discovered in a
state of dreadful commotion.  The mother, a tall, bony,
ragged shrew, had a baby tucked under one arm, while she was
using the other by the aid of a pair of dilapidated nozzleless
bellows in inflicting a tremendous beating on a howling young
gentleman of about eleven years old.  “Tut! tut! what
is the matter, Mrs. Donelly?  Rest your arm a moment, now,
and tell us all about it.”  “Matther! shure
it’s matther enough to dhrive a poor widdy beyant her
sinses!”  And then her rage turning to sorrow, she in
pathetic terms described how that she left that bad boy Johnny
only for a few moments in charge of the “darlint
comfortable ashleap in her bashket,” and that he had
neglected his duty, and that the baste of a donkey had smelt her
out, and “ate her clane out o’ bed.”</p>
<p class="calibre3">I have had so much experience in this way, that one day I may
write a book on the Haunts and Homes of the British Baby. 
It was not long after the incident of the gooseberry sieve, that
I discovered in one small room in which a family of six resided,
three little children, varying in age from three to eight,
perhaps, stark naked.  It was noon of a summer’s day,
and there they were nude as forest monkeys, and so hideously
dirty that every rib-bone in their poor wasted little bodies
showed plain, and in colour like mahogany.  Soon as I put my
head in at the door they scattered, scared as rabbits, to the
“bed,” <a id="page9"></a><span id="pgepubid00042" title="p. 9"></span>an arrangement of evil-smelling flock and old
potato-sacks, and I was informed by the mother that they had not
a rag to wear, and had been in their present condition for
<i class="calibre18">more than three months</i>.</p>
<p class="calibre3">Let us return, however, to the hordes of small Arabs found
wandering about the streets of the city.  To the mind of the
initiated, instantly recurs the question, “whence do they
all come”?  They are not imported like those other
pests of society, “German band boys or organ
grinders;” they must have been babies once upon a time;
where did they grow up?  In very dreary and retired regions,
my dear sir, though for that matter if it should happen that you
are perambulating fashionable Regent-street or aristocratic
Belgravia, when you put to yourself the perplexing question, you
may be nigher to a visible solution of the mystery than you would
care to know.  Where does the shoeless, ragged, dauntless,
and often desperate boy of the gutter breed?  Why, not
unfrequently as close almost to the mansions of the rich and
highly respectable as the sparrows in their chimney stacks. 
Nothing is more common than to discover a hideous stew of courts
and alleys reeking in poverty and wretchedness almost in the
shadow of the palatial abodes of the great and wealthy. 
Such instances might be quoted by the dozen.</p>
<p class="calibre3">It is seldom that these fledglings of the hawk tribe quit
their nests or rather their nesting places until they are
capable, although on a most limited scale, of doing business on
their own account.  Occasionally a specimen may be seen in
the vicinity of Covent Garden or Farringdon <a id="page10"></a><span id="pgepubid00043" title="p. 10"></span>Market,
seated on a carriage extemporized out of an old rusty teatray and
drawn along by his elder relatives, by means of a string. 
It may not be safely assumed, however, that the latter are
actuated by no other than affectionate and disinterested motives
in thus treating their infant charge to a ride.  It is much
more probable that being left at home in the alley by their
mother, who is engaged elsewhere at washing or
“charing,” with strict injunctions not to leave baby
for so long as a minute, and being goaded to desperation by the
thoughts of the plentiful feed of cast-out plums and oranges to
be picked up in “Common Garden” at this “dead
ripe” season of the year, they have hit on this ingenious
expedient by which the maternal mandate may be obeyed to the
letter, and their craving for market refuse be at the same time
gratified.</p>
<p class="calibre3">By-the-bye, it may here be mentioned as a contribution towards
solving the riddle, “How do these hundred thousand street
prowlers contrive to exist?” that they draw a considerable
amount of their sustenance from the markets.  And really it
would seem that by some miraculous dispensation of Providence,
garbage was for their sake robbed of its poisonous properties,
and endowed with virtues such as wholesome food possesses. 
Did the reader ever see the young market hunters at such a
“feed” say in the month of August or September? 
It is a spectacle to be witnessed only by early risers who can
get as far as Covent Garden by the time that the wholesale
dealing in the open falls slack—which will be about eight
o’clock; and it is not to be believed <a id="page11"></a><span id="pgepubid00044" title="p. 11"></span>unless it is
seen.  They will gather about a muck heap and gobble up
plums, a sweltering mass of decay, and oranges and apples that
have quite lost their original shape and colour, with the avidity
of ducks or pigs.  I speak according to my knowledge, for I
have seen them at it.  I have seen one of these gaunt
wolfish little children with his tattered cap full of plums of a
sort one of which I would not have permitted a child of mine to
eat for all the money in the Mint, and this at a season when the
sanitary authorities in their desperate alarm at the spread of
cholera had turned bill stickers, and were begging and imploring
the people to abstain from this, that, and the other, and
especially to beware of fruit unless perfectly sound and
ripe.  Judging from the earnestness with which this last
provision was urged, there must have been cholera enough to have
slain a dozen strong men in that little ragamuffin’s cap,
and yet he munched on till that frowsy receptacle was emptied,
finally licking his fingers with a relish.  It was not for
me to forcibly dispossess the boy of a prize that made him the
envy of his plumless companions, but I spoke to the market beadle
about it, asking him if it would not be possible, knowing the
propensities of these poor little wretches, so to dispose of the
poisonous offal that they could not get at it; but he replied
that it was nothing to do with him what they ate so long as they
kept their hands from picking and stealing; furthermore he
politely intimated that “unless I had nothing better to
do” there was no call for me to trouble myself about the
“little warmint,” whom nothing would hurt.  He
confided to me his private belief that they were “made
inside something after the orsestretch, <a id="page12"></a><span id="pgepubid00045" title="p. 12"></span>and that
farriers’ nails wouldn’t come amiss to ’em if
they could only get ’em down.”  However, and
although the evidence was rather in the sagacious market
beadle’s favour, I was unconverted from my original
opinion, and here take the liberty of urging on any official of
Covent Garden or Farringdon Market who may happen to read these
pages the policy of adopting my suggestion as to the safe
bestowal of fruit offal during the sickly season.  That
great danger is incurred by allowing it to be consumed as it now
is, there cannot be a question.  Perhaps it is too much to
assume that the poor little beings whom hunger prompts to feed
off garbage do so with impunity.  It is not improbable that,
in many cases, they slink home to die in their holes as poisoned
rats do.  That they are never missed from the market is no
proof of the contrary.  Their identification is next to
impossible, for they are like each other as apples in a sieve, or
peas in one pod.  Moreover, to tell their number is out of
the question.  It is as incomprehensible as is their
nature.  They swarm as bees do, and arduous indeed would be
the task of the individual who undertook to reckon up the small
fry of a single alley of the hundreds that abound in
Squalor’s regions.  They are of as small account in
the public estimation as stray street curs, and, like them, it is
only where they evince a propensity for barking and biting that
their existence is recognised.  Should death to-morrow
morning make a clean sweep of the unsightly little scavengers who
grovel for a meal amongst the market offal heaps, next day would
see the said heaps just as industriously surrounded.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0004.vue?6d4a9c55